import { useCallback, useMemo } from 'react'
import { useInfiniteQuery } from '@tanstack/react-query'
import listAudit from './api/audit/data/listAudit'
import { AuditAction, AuditCategory, AuditEntry } from './api/audit/data/AuditEntry'
import { auditLogKeys } from './queryKeys'

type UseAuditLogParams = {
  category?: AuditCategory
  action?: AuditAction
  actor?: string
  targetId?: string
  from?: string
  to?: string
  pageSize?: number
}

export type UseAuditLogResult = {
  entries: AuditEntry[]
  loadMore: () => void
  reload: () => void
  hasMore: boolean
  loading: boolean
  error: Error | null
}

const DEFAULT_PAGE_SIZE = 50

const useAuditLog = ({
  category,
  action,
  actor,
  targetId,
  from,
  to,
  pageSize = DEFAULT_PAGE_SIZE,
}: UseAuditLogParams = {}): UseAuditLogResult => {
  const query = useInfiniteQuery({
    queryKey: [
      ...auditLogKeys.all,
      category ?? '',
      action ?? '',
      actor ?? '',
      targetId ?? '',
      from ?? '',
      to ?? '',
      pageSize,
    ],
    queryFn: async ({ pageParam }: { pageParam: string | undefined }) =>
      listAudit({ cursor: pageParam, pageSize, category, action, actor, targetId, from, to }),
    getNextPageParam: (lastPage) => lastPage.next ?? undefined,
    initialPageParam: undefined,
  })

  const entries = useMemo(
    () => query.data?.pages.flatMap((page) => page.entries) ?? [],
    [query.data?.pages, query.dataUpdatedAt]
  )

  const { hasNextPage, isFetchingNextPage, fetchNextPage, refetch } = query
  const loadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) fetchNextPage()
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  const reload = useCallback(() => {
    refetch()
  }, [refetch])

  return {
    entries,
    loadMore,
    reload,
    hasMore: hasNextPage ?? false,
    loading: query.isPending || isFetchingNextPage,
    error: query.error as Error | null,
  }
}

export default useAuditLog
